import {
  useQuery,
  useInfiniteQuery,
  UseQueryResult,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { mediaLibraryService } from '@/services/media-library.service';
import {
  MediaLibraryItem,
  MediaLibraryFilters,
  StorageStats,
} from '@/types/media-library';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';

/**
 * Media Library React Query Hooks
 *
 * Hooks for managing the centralized media library (PostMedia-based)
 * Uses team-scoped query keys for proper cache isolation
 */

/**
 * List media with pagination (standard query)
 * Use this for basic pagination
 */
export const useMediaLibrary = (
  filters: MediaLibraryFilters = {},
  page = 1,
  limit = 20
): UseQueryResult<{
  items: MediaLibraryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}> => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.mediaLibrary.list(teamId, page, filters),
    queryFn: () => mediaLibraryService.list(filters, page, limit),
    enabled: hasTeam,
  });
};

/**
 * List media with infinite scroll
 * Use this for infinite scrolling UI
 */
export const useMediaLibraryInfinite = (
  filters: MediaLibraryFilters = {},
  limit = 20
): UseInfiniteQueryResult<{
  items: MediaLibraryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}> => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useInfiniteQuery({
    queryKey: [...teamQueryKeys.mediaLibrary.all(teamId), 'list', filters, 'infinite'],
    queryFn: ({ pageParam = 1 }) => mediaLibraryService.list(filters, pageParam, limit),
    getNextPageParam: (lastPage) => {
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: hasTeam,
  });
};

/**
 * Get single media item
 */
export const useMediaItem = (
  id: number | null
): UseQueryResult<MediaLibraryItem> => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.mediaLibrary.item(teamId, id!),
    queryFn: () => mediaLibraryService.getById(id!),
    enabled: hasTeam && !!id,
  });
};

/**
 * Get storage statistics
 */
export const useMediaStats = (): UseQueryResult<StorageStats> => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.mediaLibrary.stats(teamId),
    queryFn: mediaLibraryService.getStats,
    enabled: hasTeam,
  });
};
